import type { DragEndEvent } from '@dnd-kit/core';
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, createStyles } from '@mantine/core';
import { isEqual } from 'lodash-es';
import React, { useCallback } from 'react';
import type { Catalog } from '../../../api';
import { DraggableMenuItem } from './MenuItem';

export interface IMenuListProps {
	rowHeight?: number;
	disableDragging?: boolean;
	catalogProperties: Catalog['properties'];
	onColumnOrderChange: (columnName: string, toIndex: number) => void;
	onVisibilityChange: (columnName: string, isVisible: boolean) => void;
	getRightContent?: (columnName: string) => React.ReactNode;
	getSubText?: (columnName: string) => React.ReactNode;
	getLeftContent?: (columnName: string) => React.ReactNode;
}

const getListStyle = (
	rowHeight: number,
	columnCount: number
): React.CSSProperties => ({
	background: 'transparent',
	height: rowHeight * columnCount,
	overflow: 'scroll',
	maxHeight: '75vh',
});

const useStyles = createStyles((theme) => ({
	wrapper: {
		backgroundColor: theme.other.getColor('surface/primary/default'),
		'.custom-property-action-buttons': {
			display: 'none',
		},
		'&:hover': {
			'& .custom-property-action-buttons': {
				display: 'flex',
			},
		},
	},
}));

function MenuList({
	disableDragging = false,
	catalogProperties,
	onColumnOrderChange,
	onVisibilityChange,
	getRightContent,
	getSubText,
	getLeftContent,
	rowHeight = 52,
}: IMenuListProps) {
	const { classes } = useStyles();

	const [displayedCatalogProperties, setDisplayedCatalogProperties] =
		React.useState(catalogProperties);

	React.useEffect(() => {
		// This is to reduce the flickering when the columns are reordered
		// as the columns are reordered by @dnd-kit and the API
		setDisplayedCatalogProperties((prevState) => {
			if (!isEqual(catalogProperties, prevState)) {
				return catalogProperties;
			}

			return prevState;
		});
	}, [catalogProperties]);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8, // Require 8px of movement before drag starts
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;

			if (!over || active.id === over.id) {
				return;
			}

			const oldIndex = displayedCatalogProperties.findIndex(
				(item) => item.value === active.id
			);
			const newIndex = displayedCatalogProperties.findIndex(
				(item) => item.value === over.id
			);

			if (oldIndex === -1 || newIndex === -1) {
				return;
			}

			const reorderedColumns = arrayMove(
				displayedCatalogProperties,
				oldIndex,
				newIndex
			);
			setDisplayedCatalogProperties(reorderedColumns);

			onColumnOrderChange(
				displayedCatalogProperties[oldIndex]?.value,
				newIndex
			);
		},
		[displayedCatalogProperties, onColumnOrderChange]
	);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
		>
			<SortableContext
				items={displayedCatalogProperties.map((item) => item.value)}
				strategy={verticalListSortingStrategy}
			>
				<Box style={getListStyle(rowHeight, catalogProperties.length)}>
					{displayedCatalogProperties.map(({ value, label, hidden }, index) => {
						const handleVisibilityChange = (isVisible: boolean): void => {
							onVisibilityChange(value, isVisible);
						};
						const isVisible = !hidden;

						return (
							<Box className={classes.wrapper} key={value}>
								<DraggableMenuItem
									subtext={getSubText ? getSubText(value) : undefined}
									height={rowHeight}
									disableDragging={disableDragging}
									label={label || value}
									value={value}
									index={index}
									onVisibilityChange={handleVisibilityChange}
									isVisible={isVisible}
									rightContent={getRightContent && getRightContent(value)}
									leftContent={getLeftContent && getLeftContent(value)}
								/>
							</Box>
						);
					})}
				</Box>
			</SortableContext>
		</DndContext>
	);
}

export default MenuList;
